/**
 * The new-message cue.
 *
 * Synthesised rather than played from an audio file: it's two short tones, so a
 * bundled asset would mean a binary in the repo, a network fetch on first play
 * (exactly when latency is most obvious) and a `<audio>` element subject to media
 * autoplay heuristics. An oscillator has none of that and is a dozen lines.
 */

/** Two notes a fourth apart — reads as a notification rather than an alarm. */
const TONE_HZ = [880, 1174.7]
const TONE_SECONDS = 0.075
const PEAK_GAIN = 0.14

/**
 * Floor between cues. A channel that gets busy would otherwise fire one per
 * message and turn into a machine gun; the badge and the notification still
 * account for every message, this only rate-limits the audible part.
 */
const MIN_INTERVAL_MS = 3_000

let audioContext: AudioContext | null = null
let lastPlayedAt = 0
let isUnlockBound = false

type WindowWithLegacyAudio = Window & { webkitAudioContext?: typeof AudioContext }

const getAudioContext = (): AudioContext | null => {
  if (audioContext) return audioContext

  const Ctor = window.AudioContext ?? (window as WindowWithLegacyAudio).webkitAudioContext
  if (!Ctor) return null

  audioContext = new Ctor()
  return audioContext
}

/**
 * Browsers start an AudioContext suspended until the page has seen a real user
 * gesture, and a message can easily arrive before the user has clicked anything.
 * Resuming on the first interaction means the cue is armed by the time it matters,
 * instead of the first message of a session being silently dropped.
 */
export const bindMessageSoundUnlock = (): (() => void) => {
  if (isUnlockBound) return () => undefined
  isUnlockBound = true

  // Annotated because the body references `unlock` to detach itself, and TypeScript
  // cannot infer a return type through that self-reference.
  const unlock = (): void => {
    const context = getAudioContext()
    if (context?.state === 'suspended') void context.resume()
    document.removeEventListener('pointerdown', unlock)
    document.removeEventListener('keydown', unlock)
    isUnlockBound = false
  }

  document.addEventListener('pointerdown', unlock, { once: true })
  document.addEventListener('keydown', unlock, { once: true })

  return unlock
}

/**
 * Returns whether a tone was actually emitted, which the caller needs in order to
 * decide whether the OS banner should carry its own sound: this cue and the banner's
 * would otherwise both fire and one message would make two noises. Every early
 * return here is a case where the banner has to be the audible one instead.
 */
export const playMessageSound = (): boolean => {
  const now = Date.now()
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return false

  const context = getAudioContext()
  if (!context) return false

  // Still suspended means no gesture yet — staying silent is the correct outcome,
  // and calling resume() here would be a no-op anyway.
  if (context.state === 'suspended') {
    void context.resume()
    return false
  }

  lastPlayedAt = now

  TONE_HZ.forEach((frequency, index) => {
    const startAt = context.currentTime + index * TONE_SECONDS
    const endAt = startAt + TONE_SECONDS

    const oscillator = context.createOscillator()
    const gain = context.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.value = frequency

    // Ramped rather than switched: an abrupt start or stop on a sine wave is an
    // audible click, which sounds like a glitch rather than a notification.
    gain.gain.setValueAtTime(0, startAt)
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt)

    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(startAt)
    oscillator.stop(endAt)
  })

  return true
}
